import { Button, Input, InputProps } from "@fluentui/react-components";
import { useState } from "react";
import { ResourceSpaceType } from "../../APIService/ResourceSpaceTypeListService";
import "./ResourceSpaceFindSection.css";
import { FaCaretRight } from "react-icons/fa";
import { FaSortDown } from "react-icons/fa6";
import { IoAddCircleOutline } from "react-icons/io5";
import { AiOutlineMinusCircle } from "react-icons/ai";
import { PiOfficeChairFill } from "react-icons/pi";
import SelectDropDown from "../SelectDropDown";
import { Equipment, Resource, ResourceEquipment } from "../../APIService/SearchService";
import { GetMapsEnhancedResponse, GetMapsLocation } from "../../APIService/Maps";
import LocationView from "../model/UserLocationView";
import { t } from "../../utils/language";

type ResourceSpaceFindSectionProps = {
  theme: string;
  spaceType: ResourceSpaceType;
  spaceTypes: ResourceSpaceType[];
  equipmentList: Equipment[];
  tagsList: string[]
  availableResourceList: Resource[];
  selectedResourceList: Resource[];
  onChangeSearchText: (text: string) => void;
  onChangeOccupancyCount: (count: number) => void;

  //On change callback for dropdowns
  onChangeSpaceType: (spaceType: ResourceSpaceType) => void;
  onEquipmentSelect: (equipments: Equipment[]) => void;
  onTagsSelect: (tags: string[]) => void;

  //Selected
  selectedEquipmentList: Equipment[];
  selectedTagsList: string[];
  occupancyCount: number;

  addToBookingAction: (selectedResource: Resource) => void;
  initialLocation: GetMapsLocation;
  onLocationChange: (location: GetMapsLocation) => void;
  getMapsResponse: GetMapsEnhancedResponse | null;
};

const ResourceSpaceFindSection = ({
  theme,
  spaceType,
  spaceTypes,
  equipmentList,
  tagsList,
  availableResourceList,
  selectedResourceList,
  onChangeSearchText,
  onChangeSpaceType,
  onEquipmentSelect,
  onTagsSelect,
  selectedEquipmentList,
  selectedTagsList,
  onChangeOccupancyCount,
  occupancyCount,
  addToBookingAction,
  initialLocation,
  onLocationChange,
  getMapsResponse
}: ResourceSpaceFindSectionProps) => {
  const [openId, setOpenId] = useState<number | null>(null);
  const BookingSpaceDetails = ({ resource, selectedResourcesList, onAddToBooking }: { resource: Resource, selectedResourcesList: Resource[], onAddToBooking: () => void }) => {
    const isSelected = selectedResourcesList.some(
      item => item.Id === resource.Id
    );
    const isOpen = openId === resource.Id;

    return (
      <div className="avaliable-spaceContainer">
        <div className="avaliable-space">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-evenly",
            }}
          >
            {isOpen ? (
              <FaSortDown
                size={14}
                style={{ marginRight: 10, cursor: "pointer" }}
                onClick={() => setOpenId(null)}
              />
            ) : (
              <FaCaretRight
                size={12}
                style={{ marginRight: 10, cursor: "pointer" }}
                onClick={() => setOpenId(resource.Id)}
              />
            )}

            <span>
              {resource.ResName}
            </span>

            {!isOpen && (
              <>
                <span className="vertical-line" />

                <div style={{ display: "flex", alignItems: "center" }}>
                  <PiOfficeChairFill />
                  <span style={{ marginLeft: 5 }}>{resource.Occupancy}</span>
                </div>

                <span className="vertical-line" />

                <div className="header-icon">
                  {resource.Equipment?.map((eq: ResourceEquipment) => (
                    <div
                      key={eq.EquipmentId}
                      style={{
                        backgroundColor: "#454545",
                        padding: 5,
                        borderRadius: 2,
                        margin: 3,
                      }}
                    >
                      <img
                        src={localStorage.getItem("tenantUrl") + ((eq.Status && eq.Status === 2) ? eq.FaultImagePath : eq.ImagePath)}
                        alt={eq.Name}
                        style={{ height: 16, width: 16 }}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <Button style={{ backgroundColor: isSelected ? "#124779" : "#1A1A1A" }} size="small" shape="circular" onClick={onAddToBooking}>
            {isSelected ? `${t('AddedToBooking')}` : `${t('AddToBooking')}`}
            {/* {isSelected ? 'Added To Booking' : 'Add To Booking'} */}
          </Button>
        </div>

        {isOpen && (
          <>
            <div className="space-details">
              <PiOfficeChairFill />
              <span style={{ marginLeft: 5 }}>{resource.Occupancy}</span>

              {resource.Equipment?.map((eq: any) => (
                <Button
                  key={eq.EquipmentId}
                  size="small"
                  icon={
                    <img
                      src={localStorage.getItem("tenantUrl") + eq.ImagePath}
                      style={{ height: 15, width: 15 }}
                    />
                  }
                  iconPosition="before"
                  style={{ marginLeft: 5 }}
                >
                  {eq.Name}
                </Button>
              ))}
            </div>

            {resource.RoomImage && (
              <div className="space-details">
                <img
                  src={localStorage.getItem("tenantUrl") + resource.RoomImage}
                  style={{
                    width: "20%",
                    borderRadius: 10,
                  }}
                />
              </div>
            )}
          </>
        )}
        <hr />
      </div>
    );
  };

  const onChange: InputProps["onChange"] = (ev, data) => {
    onChangeSearchText(data.value);
  }

  return (
    <div className="findContainer">
      <div className="filterOptionsContainer">
        <div style={{ height: 34, padding: "4px 4px 5px 4px", border: theme === "dark" ? "1px solid #525252" : "1px solid #EAEFF5", borderRadius: '3px', boxSizing: "border-box", display: "flex", alignItems: "center" }}>
          <Input
            placeholder={t('SearchPlaceHolder')}
            size="small"
            style={{ width: "100%", height: 23, fontSize: 13, border: '0px' }}
            onChange={onChange}
          />
        </div>

        <div style={{ height: "23px", gap: "5px", display: "flex", alignItems: "center" }}>
          <LocationView 
            theme={theme} 
            userLocation={initialLocation} 
            allLocations={getMapsResponse?.Locations ?? []}
            onLocationChange={onLocationChange}
          />
        </div>

        <div className="dropdown-row">
          <SelectDropDown<ResourceSpaceType>
            data={spaceTypes}
            placeholder={t('SpaceType')}
            getId={item => item.id.toString()}
            getLabel={item => item.name}
            getIcon={item => item.icon ?? undefined}
            defaultValue={spaceType}
            onChange={(selected) => {
              const space = selected.item;
              onChangeSpaceType(space);
            }}
          />

          <SelectDropDown<Equipment>
            data={equipmentList}
            placeholder={t('Equipment')}
            getId={item => item.id.toString()}
            getLabel={item => item.name}
            getIcon={item => item.icon ?? undefined}
            multiselect
            defaultValue={selectedEquipmentList}
            onChange={(selected) => {
              const equipments = selected.map(s => s.item);
              onEquipmentSelect(equipments);
            }}
          />

          <SelectDropDown<string>
            data={tagsList}
            placeholder={t('Tags')}
            getId={item => item}
            getLabel={item => item}
            multiselect
            defaultValue={selectedTagsList}
            onChange={(selected) => {
              const tags = selected.map(s => s.item);
              onTagsSelect(tags);
            }}
          />

          <Button
            className="occupancyButton"
            appearance="outline"
            shape="circular"
            size="small"
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <span
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
              }}
            >
              {t('Occupancy')}
            </span>
            <AiOutlineMinusCircle
              onClick={() =>
                occupancyCount > 0 ? onChangeOccupancyCount(occupancyCount - 1) : 0
              }
            />
            <span>{occupancyCount}</span>
            <IoAddCircleOutline
              onClick={() => onChangeOccupancyCount(occupancyCount + 1)}
            />
          </Button>
        </div>
      </div>
      <div className="spaceTypeContainer">
        {spaceType.icon && (
          <img
            className="spaceType-icon"
            src={localStorage.getItem("tenantUrl") + spaceType.icon}
            alt=""
          />
        )}
        <p>{spaceType.name}</p>
      </div>
      <div className="resultsScroll">
        {availableResourceList.map((resource: Resource) => (
          <BookingSpaceDetails
            key={resource.Id}
            resource={resource}
            selectedResourcesList={selectedResourceList}
            onAddToBooking={() => addToBookingAction(resource)}
          />
        ))}
      </div>
    </div>
  );
};

export default ResourceSpaceFindSection;
